import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Link,
  Divider,
  Alert,
  Stack,
  IconButton,
  InputAdornment
} from '@mui/material';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth } from '../config/firebase.js';
import { useDispatch } from 'react-redux';
import { setError } from '../store/slices/authSlice';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { styled } from '@mui/material/styles';
import {
  Visibility,
  VisibilityOff,
  Google as GoogleIcon,
  Facebook as FacebookIcon,
} from '@mui/icons-material';
import { useFirebaseAuth } from '../hooks/useFirebaseAuth';
import CircularProgress from '@mui/material/CircularProgress';
import { motion } from 'framer-motion';

const AuthContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.7)), url('/images/auth-bg.jpg')`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  padding: theme.spacing(3),
}));

const AuthPaper = styled(motion.div)(({ theme }) => ({
  padding: theme.spacing(4),
  width: '100%',
  maxWidth: 450,
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  borderRadius: theme.spacing(2),
  backdropFilter: 'blur(10px)',
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
}));

const SocialButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
}));

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      when: "beforeChildren",
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 }
  }
};

function Register() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [registerError, setRegisterError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState('');
  
  const { googleLogin, facebookLogin } = useFirebaseAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setRegisterError('');

    // Validation
    const nameError = validateField('name', formData.name);
    const emailError = validateField('email', formData.email);
    const passwordError = validateField('password', formData.password);
    const confirmPasswordError = validateField('confirmPassword', formData.confirmPassword);

    if (nameError || emailError || passwordError || confirmPasswordError) {
      setErrors({
        name: nameError,
        email: emailError,
        password: passwordError,
        confirmPassword: confirmPasswordError
      });
      return;
    }

    setIsLoading(true);
    try {
      // Create user
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // Update profile with name
      await updateProfile(userCredential.user, {
        displayName: formData.name
      });

      navigate('/');
    } catch (error) {
      console.error('Registration error:', error);
      let errorMessage = 'Registration failed';
      
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'Email is already registered';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address';
          break;
        default:
          errorMessage = error.message;
      }
      
      dispatch(setError(errorMessage));
      setRegisterError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'name':
        if (!value) error = 'Name is required';
        break;
      case 'email':
        if (!value) {
          error = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          error = 'Invalid email address';
        }
        break;
      case 'password':
        if (!value) {
          error = 'Password is required';
        } else if (value.length < 6) {
          error = 'Password must be at least 6 characters';
        }
        break;
      case 'confirmPassword':
        if (!value) {
          error = 'Please confirm your password';
        } else if (value !== formData.password) {
          error = 'Passwords do not match';
        }
        break;
      default:
        break;
    }
    return error;
  };

  const handleSocialLogin = async (provider) => {
    setSocialLoading(provider);
    try {
      if (provider === 'google') {
        await googleLogin();
      } else if (provider === 'facebook') {
        await facebookLogin();
      }
      navigate('/');
    } catch (error) {
      console.error('Social login error:', error);
      let errorMessage = 'Social login failed';
      
      switch (error.code) {
        case 'auth/account-exists-with-different-credential':
          errorMessage = 'Account exists with different credentials';
          break;
        case 'auth/operation-not-allowed':
          errorMessage = 'Operation not allowed';
          break;
        default:
          errorMessage = error.message;
      }
      
      dispatch(setError(errorMessage));
      setRegisterError(errorMessage);
    } finally {
      setSocialLoading('');
    }
  };

  return (
    <AuthContainer>
      <Container maxWidth="sm">
        <AuthPaper
          elevation={3}
          component={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants}>
            <Typography variant="h4" align="center" gutterBottom fontWeight="bold">
              Create Account
            </Typography>
          </motion.div>

          <Typography variant="body1" align="center" color="text.secondary">
            Join RentWheels today
          </Typography>

          {registerError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {registerError}
            </Alert>
          )}

          <motion.div variants={itemVariants}>
            <Stack spacing={2} component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                margin="normal"
                required
                sx={{ mb: 2 }}
                error={!!errors.name}
                helperText={errors.name}
              />
              
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                margin="normal"
                required
                sx={{ mb: 2 }}
                error={!!errors.email}
                helperText={errors.email}
              />
              
              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                margin="normal"
                required
                error={!!errors.password}
                helperText={errors.password || "Password must be at least 6 characters"}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <TextField
                fullWidth
                label="Confirm Password"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                margin="normal"
                required
                sx={{ mb: 3 }}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isLoading}
                sx={{
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  background: '#800020',
                  boxShadow: '0 3px 5px 2px rgba(128, 0, 32, 0.3)',
                  '&:hover': {
                    background: 'rgba(128, 0, 32, 0.9)',
                    boxShadow: '0 4px 8px rgba(128, 0, 32, 0.2)',
                    transform: 'translateY(-1px)',
                    transition: 'all 0.3s ease',
                  }
                }}
                startIcon={<PersonAddIcon />}
              >
                {isLoading ? 'Creating Account...' : 'Create Account'}
              </Button>

              <Box sx={{ my: 2 }}>
                <Divider>
                  <Typography variant="body2" color="text.secondary">
                    OR
                  </Typography>
                </Divider>
              </Box>

              <Stack direction="row" spacing={2}>
                <SocialButton
                  fullWidth
                  startIcon={socialLoading === 'google' ? 
                    <CircularProgress size={20} /> : 
                    <GoogleIcon />
                  }
                  onClick={() => handleSocialLogin('google')}
                  disabled={!!socialLoading}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#f1f3f4',
                    }
                  }}
                >
                  Google
                </SocialButton>
                <SocialButton
                  fullWidth
                  startIcon={socialLoading === 'facebook' ? 
                    <CircularProgress size={20} /> : 
                    <FacebookIcon />
                  }
                  onClick={() => handleSocialLogin('facebook')}
                  disabled={!!socialLoading}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#f0f2f5',
                    }
                  }}
                >
                  Facebook
                </SocialButton>
              </Stack>
            </Stack>
          </motion.div>

          <Typography variant="body2" align="center" sx={{ mt: 2 }}>
            Already have an account?{' '}
            <Button
              component={RouterLink}
              to="/login"
              color="primary"
              sx={{ fontWeight: 'bold' }}
            >
              Sign In
            </Button>
          </Typography>
        </AuthPaper>
      </Container>
    </AuthContainer>
  );
}

export default Register; 